import { useState, useEffect } from 'react';
import { Menu, X, User, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { AuthModal } from '@/components/auth/AuthModal';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { getTotalItems } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const cartItemsCount = getTotalItems();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Previeni lo scroll del body quando il menu mobile è aperto
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Catalogo', href: '/catalogo' },
    { label: 'Chi Siamo', href: '/#chi-siamo' },
    { label: 'Contatti', href: '/#contatti' },
  ];

  return (
    <>
      <header
        role="banner"
        className={`fixed left-0 right-0 top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-background/95 backdrop-blur-sm py-3 md:py-4' 
            : 'bg-transparent py-4 md:py-6'
        } ${isMobileMenuOpen ? 'border-b-0' : ''}`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-6">
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0" aria-label="Brace e Sapori - Torna alla home">
            <img 
              src="/logo.png" 
              alt="Brace e Sapori Store" 
              className={`h-12 w-auto md:h-12 object-contain transition-all duration-300 ease-out ${
                isMobileMenuOpen ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
              }`}
              width="120"
              height="48"
              loading="eager"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-8 lg:flex" role="navigation" aria-label="Navigazione principale">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-light uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground underline-hover"
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <User className="h-4 w-4 mr-2" />
                    {user?.displayName || 'Account'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.displayName}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <a href="/account">
                      <User className="mr-2 h-4 w-4" />
                      Il Mio Account
                    </a>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setIsAuthModalOpen(true)}>
                <User className="h-4 w-4 mr-2" />
                Accedi
              </Button>
            )}
            
            {/* Cart Button */}
            <Button variant="outline" size="sm" asChild className="relative">
              <a href="/carrello" aria-label="Vai al carrello">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Carrello
                {cartItemsCount > 0 && (
                  <Badge className="absolute -right-2 -top-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                    {cartItemsCount}
                  </Badge>
                )}
              </a>
            </Button>
          </div>

          {/* Mobile Actions */}
          <div className="flex items-center gap-2 lg:hidden">
            {/* User Button - Mobile */}
            {isAuthenticated ? (
              <Button variant="outline" size="sm" onClick={logout}>
                <LogOut className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setIsAuthModalOpen(true)}>
                <User className="h-4 w-4" />
              </Button>
            )}
            
            {/* Carrello Button - Mobile */}
            <Button variant="outline" size="sm" asChild className="hidden sm:flex relative">
              <a href="/carrello" aria-label="Vai al carrello">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {cartItemsCount > 0 && (
                  <Badge className="absolute -right-1 -top-1 h-4 w-4 rounded-full p-0 flex items-center justify-center text-[10px]">
                    {cartItemsCount}
                  </Badge>
                )}
              </a>
            </Button>
            
            {/* Mobile Menu Button */}
            <button
              className="text-foreground z-[60] relative shrink-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Chiudi menu" : "Apri menu"}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - Fuori dall'header per evitare conflitti di z-index */}
      {isMobileMenuOpen && (
        <div 
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Menu di navigazione mobile"
          className="fixed inset-0 z-[100] bg-background lg:hidden overflow-hidden"
          style={{
            animation: 'fadeIn 0.3s ease-out forwards',
          }}
          onClick={(e) => {
            // Chiudi il menu se si clicca sullo sfondo
            if (e.target === e.currentTarget) {
              setIsMobileMenuOpen(false);
            }
          }}
        >
          {/* Header del menu mobile con solo close button */}
          <div 
            className="flex items-center justify-end px-4 md:px-6 py-4 md:py-6 bg-background shrink-0"
/*             style={{
              animation: 'slideInFromRight 0.4s ease-out forwards',
            }} */
          >
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-foreground hover:text-primary transition-colors shrink-0 pt-3"
/*               style={{
                animation: 'zoomIn 0.3s ease-out 0.2s forwards',
                opacity: 0,
              }} */
              aria-label="Chiudi menu"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          
          {/* Contenuto del menu scrollabile */}
          <div className="h-[calc(100vh-73px)] overflow-y-auto overscroll-contain -webkit-overflow-scrolling-touch">
            <nav className="flex flex-col items-center justify-center gap-6 md:gap-8 py-8 md:py-16 px-4 md:px-6 min-h-full" role="navigation" aria-label="Menu di navigazione">
              {navLinks.map((link, index) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-base md:text-lg font-light uppercase tracking-wider text-muted-foreground transition-all hover:text-foreground text-center"
                  style={{
                    animation: `slideInFromBottom 0.5s ease-out ${200 + index * 50}ms forwards`,
                    opacity: 0,
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <Button 
                variant="hero" 
                size="lg" 
                asChild 
                className="mt-4 w-full max-w-xs"
                style={{
                  animation: `slideInFromBottom 0.5s ease-out ${200 + navLinks.length * 50}ms forwards`,
                  opacity: 0,
                }}
              >
                <a href="/carrello" onClick={() => setIsMobileMenuOpen(false)} aria-label="Vai al carrello">
                  Vai al Carrello
                </a>
              </Button>
            </nav>
          </div>
        </div>
      )}
      
      {/* Auth Modal */}
      <AuthModal open={isAuthModalOpen} onOpenChange={setIsAuthModalOpen} />
    </>
  );
};
